// src/components/BareMetal.jsx
import React from "react";
import {
  Server,
  Cpu,
  HardDrive,
  ShieldCheck,
  Network,
  Gauge,
} from "lucide-react";
import { Link } from "react-router-dom";

import SlideInOnScroll from "./SlideInOnScroll";
import FadeInCard from "./FadeInCard";
import FadeInOnScroll from "./FadeInOnScroll";

export default function BareMetal() {
  const specs = [
    "Single-tenant dedicated servers — no virtualization layer.",
    "Configurable CPU, memory, NVMe/SAS storage and GPU.",
    "Full root access with out-of-band management.",
    "Choice of OS images or custom ISO.",
  ];

  const useCases = [
    "High-frequency trading and ultra-low latency workloads",
    "Large databases and storage systems",
    "Licensing-restricted software and compliance-sensitive workloads",
    "Hybrid architectures combining Bare Metal with Cloud GPU/Compute",
  ];

  return (
    <main className="relative overflow-hidden bg-gradient-to-b from-purple-50 via-white to-purple-100 text-[#0f0533]">
      {/* 背景柔光 */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-24 -top-10 h-[26rem] w-[26rem] rounded-full bg-purple-300/25 blur-3xl" />
        <div className="absolute right-[-10rem] top-40 h-[30rem] w-[30rem] rounded-full bg-purple-200/30 blur-3xl" />
        <div className="absolute bottom-[-8rem] left-1/3 h-[22rem] w-[22rem] rounded-full bg-fuchsia-200/30 blur-3xl" />
      </div>

      {/* HERO */}
      <section className="relative max-w-6xl mx-auto px-6 pt-28 pb-24 grid gap-12 md:grid-cols-[1.4fr_1fr] items-center">
        <SlideInOnScroll from="left">
          <div>
            <p className="text-sm tracking-[0.3em] font-semibold text-[#7b5cff] mb-3 uppercase">
              Bare Metal
            </p>
            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-4 bg-gradient-to-r from-[#5b21b6] via-[#7c3aed] to-[#a855f7] bg-clip-text text-transparent">
              Dedicated Bare Metal
              <br />
              for maximum control
            </h1>
            <p className="text-base md:text-lg text-[#3b2c70] max-w-xl">
              Run your most demanding and compliance-sensitive workloads on
              fully dedicated servers—with no virtualization layer and complete
              control over the stack.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                className="rounded-full bg-gradient-to-r from-[#6a2dd9] to-[#9a8dfc] px-6 py-3 text-sm font-semibold text-white shadow-[0_10px_35px_rgba(106,45,217,0.35)] hover:translate-y-[-2px] transition-transform inline-flex items-center justify-center"
              >
                Configure a Bare Metal server
              </Link>
              <p className="text-xs md:text-sm text-[#5b4a99]">
                Custom configurations with hourly or monthly terms.
              </p>
            </div>
          </div>
        </SlideInOnScroll>

        <SlideInOnScroll from="right">
          <div className="hidden md:block rounded-3xl bg-white/80 backdrop-blur-xl border border-purple-200 p-7 shadow-[0_18px_50px_rgba(148,101,255,0.25)]">
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#a855f7] mb-2">
              WHY BARE METAL
            </p>
            <ul className="space-y-2 text-sm text-[#4b3a82]">
              {specs.map((s) => (
                <li key={s}>• {s}</li>
              ))}
            </ul>
          </div>
        </SlideInOnScroll>
      </section>

      {/* HARDWARE HIGHLIGHTS */}
      <section className="relative py-18 mb-10">
        <div className="max-w-6xl mx-auto px-6 grid gap-10 md:grid-cols-3">
          {[
            {
              title: "Latest generation CPUs",
              desc: "Choose from high-core-count and high-frequency CPU options to match your compute needs.",
              icon: <Cpu className="w-6 h-6 text-[#7c3aed]" />,
            },
            {
              title: "Flexible storage layouts",
              desc: "Mix NVMe, SSD and HDD to build the right balance of throughput, IOPS and capacity.",
              icon: <HardDrive className="w-6 h-6 text-[#7c3aed]" />,
            },
            {
              title: "Deterministic performance",
              desc: "With no noisy neighbors or shared hypervisor, your application gets the full power of the server.",
              icon: <Gauge className="w-6 h-6 text-[#7c3aed]" />,
            },
          ].map((card, index) => (
            <FadeInCard key={card.title} delay={index * 0.15}>
              <div className="rounded-3xl bg-white/85 border border-purple-200 p-7 shadow-[0_12px_35px_rgba(148,101,255,0.22)] min-h-[220px]">
                <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center mb-3">
                  {card.icon}
                </div>
                <h3 className="text-md font-semibold text-[#2a0f4c] mb-1">
                  {card.title}
                </h3>
                <p className="text-sm text-[#4b3a82] leading-relaxed">
                  {card.desc}
                </p>
              </div>
            </FadeInCard>
          ))}
        </div>
      </section>

      {/* USE CASES & NETWORKING */}
      <section className="relative py-20 bg-gradient-to-b from-purple-50 via-white to-purple-100">
        <div className="max-w-6xl mx-auto px-6 grid gap-10 md:grid-cols-2 items-start">
          {/* Use cases */}
          <SlideInOnScroll from="left">
            <div className="rounded-3xl bg-gradient-to-br from-[#f5e9ff] via-white to-[#ecebff] border border-purple-200 p-8 shadow-[0_14px_40px_rgba(148,101,255,0.24)]">
              <div className="flex items-center gap-3 mb-4">
                <Server className="w-7 h-7 text-[#7c3aed]" />
                <h3 className="text-xl font-bold text-[#2a0f4c]">
                  Ideal for specialized workloads
                </h3>
              </div>
              <p className="text-md text-[#3b2c70] mb-4">
                Bare Metal servers are designed for scenarios where you need
                strict isolation, specialized licensing or maximum performance.
              </p>
              <ul className="text-sm text-[#4b3a82] space-y-2">
                {useCases.map((u) => (
                  <li key={u}>• {u}</li>
                ))}
              </ul>
            </div>
          </SlideInOnScroll>

          {/* Network & security */}
          <SlideInOnScroll from="right">
            <div className="space-y-6">
              <FadeInCard delay={0}>
                <div className="rounded-3xl bg-white/90 border border-purple-200 p-6 shadow-[0_10px_30px_rgba(148,101,255,0.2)] flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                    <Network className="w-6 h-6 text-[#7c3aed]" />
                  </div>
                  <div>
                    <h4 className="text-md font-semibold text-[#2a0f4c] mb-1">
                      High-capacity networking
                    </h4>
                    <p className="text-sm text-[#4b3a82] leading-relaxed">
                      Connect Bare Metal servers to Cloud Compute and Cloud GPU
                      via private networking to build hybrid clusters with
                      flexible scaling.
                    </p>
                  </div>
                </div>
              </FadeInCard>

              <FadeInCard delay={0.15}>
                <div className="rounded-3xl bg-white/90 border border-purple-200 p-6 shadow-[0_10px_30px_rgba(148,101,255,0.2)] flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                    <ShieldCheck className="w-6 h-6 text-[#7c3aed]" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-[#2a0f4c] mb-1">
                      Security & compliance ready
                    </h4>
                    <p className="text-xs text-[#4b3a82] leading-relaxed">
                      Dedicated hardware helps meet strict regulatory
                      requirements, while network isolation, firewalls and
                      access controls protect your environment.
                    </p>
                  </div>
                </div>
              </FadeInCard>
            </div>
          </SlideInOnScroll>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 bg-gradient-to-r from-[#b892ff]/15 via-white to-[#e0d2ff]/35">
        <FadeInOnScroll>
          <div className="max-w-6xl mx-auto px-6 rounded-3xl bg-white/80 border border-purple-200 shadow-[0_20px_60px_rgba(148,101,255,0.25)] flex flex-col md:flex-row items-center justify-between gap-8 px-8 py-10">
            <div className="space-y-2">
              <p className="text-xs tracking-[0.18em] uppercase text-[#9b6fff]">
                Dedicated infrastructure
              </p>
              <h3 className="text-xl md:text-2xl font-bold text-[#3d1a6e]">
                Design your next platform on AI NOVA Bare Metal
              </h3>
              <p className="text-sm text-[#4c3670] max-w-xl">
                Work with our team to choose the right hardware profile and
                network design for your most demanding workloads.
              </p>
            </div>
            <Link to="/contact">
              <button className="rounded-full bg-[#8b5cf6] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#7c3aed] transition">
                Talk to us 
              </button>
            </Link>
          </div>
        </FadeInOnScroll>
      </section>
    </main>
  );
}
